import { Controller } from "@hotwired/stimulus"

// Controlador genérico para upload de medias multi-tipo.
// Soporta: imagen, documento (upload directo), video, audio (chunked via Mediastream).
//
// Dos modos de operación:
//   - Normal (pending=false): sube y crea MediaAttachment inmediatamente.
//     Respuesta via Turbo Stream que append el thumbnail.
//   - Pending (pending=true): sube sin crear attachment.
//     Retorna JSON, muestra preview + hidden field con media_id.
//     Usado en formularios donde el owner no existe aún (ej: comentarios).
//
// Flujo normal:
//   1. Usuario clickea "Subir archivo" → abre file picker
//   2. Imagen/documento: POST multipart → Turbo Stream append
//   3. Video/audio: create_vod → chunks → attach_vod → Turbo Stream append
//
// Flujo pending:
//   1. Usuario clickea "Subir archivo" → abre file picker
//   2. Imagen/documento: POST multipart a create_pending → JSON → preview + hidden field
//   3. Video/audio: create_vod → chunks → (sin attach_vod) → preview + hidden field
//   4. Submit del form → media_ids[] van con el POST
export default class extends Controller {
  static targets = [
    "fileInput",        // <input type="file"> oculto
    "progressSection",  // Contenedor de la barra de progreso
    "progressBar",      // Barra de progreso (inner fill)
    "progressText",     // Texto "%"
    "uploadBtn",        // Botón "Subir archivo"
    "errorSection",     // Sección de error
    "list",             // Contenedor de thumbnails (normal y pending)
  ]

  static values = {
    createUrl: String,           // POST multipart (imagen/documento) — modo normal
    createPendingUrl: String,    // POST multipart (imagen/documento) — modo pending
    createVodUrl: String,        // POST JSON (video/audio → crear en Mediastream)
    uploadUrlEndpoint: String,   // POST JSON (obtener URL de chunks)
    attachVodUrl: String,        // POST JSON (attach media después del chunked upload) — modo normal
    csrfToken: String,
    accept: { type: String, default: ".jpg,.jpeg,.png,.gif,.mp4,.mov,.mp3,.pdf,.doc,.docx" },
    chunkSize: { type: Number, default: 1000000 },
    objectType: String,
    objectId: String,
    pending: { type: Boolean, default: false },
    maxSize: { type: Number, default: 0 },       // bytes; 0 = sin limite
    maxSizeMessage: { type: String, default: "" }, // admite %{size}
  }

  connect() {
    this.file = null
    this.uploading = false
    this.boundFetchResponse = this.onTurboFetchResponse.bind(this)
    this.element.addEventListener("turbo:before-fetch-response", this.boundFetchResponse)
  }

  disconnect() {
    this.element.removeEventListener("turbo:before-fetch-response", this.boundFetchResponse)
  }

  // Captura el delete del thumb (link con turbo-method) — un fetch Turbo
  // exitoso dentro del wrapper marca dirty el header del editor.
  onTurboFetchResponse(event) {
    if (event.detail?.fetchResponse?.response?.ok) {
      this.notifyRecordSaved()
    }
  }

  // --- Actions ---

  triggerFileInput() {
    if (this.uploading) return
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ""
      this.fileInputTarget.click()
    }
  }

  // Remueve un preview pending pre-renderizado desde ERB (edicion de formulario).
  // Solo client-side: la baja real se aplica en el controller al recibir media_ids[]
  // y diffear contra los medias persistidos via assign_medias!.
  removePending(event) {
    event.preventDefault()
    const wrapper = event.currentTarget.closest("[id^='pending_media_']")
    if (wrapper) wrapper.remove()
  }

  async fileSelected(event) {
    this.file = event.target.files[0]
    if (!this.file) return

    this.hideError()

    const mediaType = this.detectMediaType(this.file.type)

    // Imagen/documento van por POST multipart directo: un archivo grande muere
    // en el 413 del proxy. Video/audio suben por chunks, asi que no aplica.
    if (mediaType !== "vod" && mediaType !== "audio" && this.exceedsMaxSize()) {
      this.showError(this.maxSizeMessageValue.replace("%{size}", this.formatSize(this.file.size)))
      return
    }

    this.uploading = true
    this.showProgress()
    this.disableUploadBtn()

    try {
      if (mediaType === "vod" || mediaType === "audio") {
        await this.uploadVod(mediaType)
      } else {
        await this.uploadDirect()
      }
    } catch (error) {
      console.error("[media-upload] Error:", error)
      this.showError(error.message || "Error al subir archivo")
    } finally {
      this.hideProgress()
      this.enableUploadBtn()
      this.uploading = false
    }
  }

  // --- Upload directo (imagen/documento) ---

  async uploadDirect() {
    this.updateProgressUI(10)

    const formData = new FormData()
    formData.append("file", this.file)

    if (this.pendingValue) {
      // Modo pending: POST a create_pending → JSON
      const response = await fetch(this.createPendingUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json",
        },
        body: formData,
      })

      this.updateProgressUI(90)
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `HTTP ${response.status}`)
      }

      this.updateProgressUI(100)
      const result = await response.json()
      this.addPendingPreview(result)
    } else {
      // Modo normal: POST a create → Turbo Stream
      formData.append("object_type", this.objectTypeValue)
      formData.append("object_id", this.objectIdValue)

      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "text/vnd.turbo-stream.html",
        },
        body: formData,
      })

      this.updateProgressUI(90)
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `HTTP ${response.status}`)
      }

      const html = await response.text()
      this.updateProgressUI(100)
      Turbo.renderStreamMessage(html)
      this.notifyRecordSaved()
    }
  }

  // --- Upload chunked (video/audio) ---

  async uploadVod(mediaType) {
    this.updateProgressUI(0)

    // Step 1: Crear media en Mediastream
    const createBody = { media_type: mediaType }
    if (!this.pendingValue) {
      createBody.object_type = this.objectTypeValue
      createBody.object_id = this.objectIdValue
    }

    const createResponse = await this.postJSON(this.createVodUrlValue, createBody)
    const { media_id, vod_media_id } = createResponse
    if (!vod_media_id) throw new Error("No se pudo crear el media")

    this.updateProgressUI(5)

    // Step 2: Obtener URL del servidor de chunks
    const uploadBody = {
      file_name: this.file.name,
      media_id: vod_media_id,
      size: this.file.size,
    }
    if (!this.pendingValue) {
      uploadBody.object_type = this.objectTypeValue
      uploadBody.object_id = this.objectIdValue
    }

    const uploadResponse = await this.postJSON(this.uploadUrlEndpointValue, uploadBody)
    const chunkServerUrl = uploadResponse.url
    if (!chunkServerUrl) throw new Error("No se obtuvo URL de upload")

    // Step 3: Subir chunks secuencialmente (5% a 90%)
    await this.uploadChunks(chunkServerUrl)

    if (this.pendingValue) {
      // Modo pending: no attach, solo preview + hidden field
      this.updateProgressUI(100)
      this.addPendingPreview({
        media_id,
        media_type: mediaType,
        preview_url: null,
        name: this.file.name,
      })
    } else {
      // Modo normal: attach_vod → Turbo Stream
      this.updateProgressUI(95)
      const attachResponse = await fetch(this.attachVodUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "text/vnd.turbo-stream.html",
        },
        body: JSON.stringify({
          media_id,
          object_type: this.objectTypeValue,
          object_id: this.objectIdValue,
        }),
      })

      if (!attachResponse.ok) {
        throw new Error(`Error al vincular media: HTTP ${attachResponse.status}`)
      }

      const html = await attachResponse.text()
      this.updateProgressUI(100)
      Turbo.renderStreamMessage(html)
      this.notifyRecordSaved()
    }
  }

  // Notifica al editor de paginas (manage--editor-draft-store) que hubo un
  // cambio persistido para marcar el header como dirty. No-op fuera del editor.
  notifyRecordSaved() {
    document.dispatchEvent(new CustomEvent("editor:record-saved"))
  }

  async uploadChunks(serverUrl) {
    const chunkSize = this.chunkSizeValue
    const totalChunks = Math.ceil(this.file.size / chunkSize)

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize
      const end = Math.min(start + chunkSize, this.file.size)
      const blob = this.file.slice(start, end)
      const chunkIndex = i + 1

      const formData = new FormData()
      formData.append("start", start)
      formData.append("end", end)
      formData.append("file", blob)

      const response = await fetch(`${serverUrl}/${chunkIndex}`, {
        method: "POST",
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Chunk ${chunkIndex} falló: ${response.status}`)
      }

      const percent = 5 + Math.ceil((chunkIndex * 85) / totalChunks)
      this.updateProgressUI(percent)
    }
  }

  // --- Pending preview: thumbnail + hidden field + remove button ---

  addPendingPreview({ media_id, media_type, preview_url, name }) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative"
    wrapper.id = `pending_media_${media_id}`

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = "media_ids[]"
    hidden.value = media_id
    wrapper.appendChild(hidden)

    const thumb = document.createElement("div")
    // Mantener sincronizadas estas clases con MediaUploaderThumbHelper::THUMB_WRAPPER_CLASSES.
    thumb.className = "w-[80px] h-[80px] rounded-[6px] overflow-hidden bg-[#f4f4f4] border border-black/10"

    if (media_type === "image" && preview_url) {
      const img = document.createElement("img")
      img.src = preview_url
      img.className = "w-full h-full object-cover"
      thumb.appendChild(img)
    } else {
      // Icono fallback — replica MediaViewerHelper#media_compact_icon (icono FA centrado).
      const iconClass = media_type === "vod" ? "fa-regular fa-video"
        : media_type === "audio" ? "fa-regular fa-microphone"
        : media_type === "document" ? "fa-regular fa-file"
        : "fa-regular fa-image"
      thumb.innerHTML = `<div class="flex items-center justify-center h-full"><i class="${iconClass} text-[20px] text-[#999]"></i></div>`
    }
    wrapper.appendChild(thumb)

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    // Mantener sincronizadas estas clases con MediaUploaderThumbHelper::REMOVE_BTN_CLASSES.
    removeBtn.className = "absolute top-[-4px] right-[-4px] z-10 w-[20px] h-[20px] rounded-full bg-black hover:bg-[#333] text-white text-[10px] inline-flex items-center justify-center"
    removeBtn.innerHTML = '<i class="fa-solid fa-xmark"></i>'
    removeBtn.addEventListener("click", () => wrapper.remove())
    wrapper.appendChild(removeBtn)

    if (this.hasListTarget) {
      this.listTarget.appendChild(wrapper)
    }
  }

  // --- Helpers de tipo ---

  detectMediaType(mimeType) {
    if (mimeType.startsWith("video/")) return "vod"
    if (mimeType.startsWith("audio/")) return "audio"
    if (mimeType.startsWith("image/")) return "image"
    return "document"
  }

  exceedsMaxSize() {
    return this.maxSizeValue > 0 && this.file.size > this.maxSizeValue
  }

  // Formatea bytes igual que Document.human_size (1 MB = 1.048.576 bytes).
  formatSize(bytes) {
    const mb = bytes / 1048576
    if (mb >= 1) return `${mb.toFixed(1)} MB`
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // --- UI Helpers ---

  showProgress() {
    if (this.hasProgressSectionTarget) this.progressSectionTarget.classList.remove("hidden")
    this.updateProgressUI(0)
  }

  hideProgress() {
    if (this.hasProgressSectionTarget) this.progressSectionTarget.classList.add("hidden")
  }

  updateProgressUI(percent) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percent}%`
    }
  }

  disableUploadBtn() {
    if (this.hasUploadBtnTarget) {
      this.uploadBtnTarget.disabled = true
      this.uploadBtnTarget.classList.add("opacity-50", "pointer-events-none")
    }
  }

  enableUploadBtn() {
    if (this.hasUploadBtnTarget) {
      this.uploadBtnTarget.disabled = false
      this.uploadBtnTarget.classList.remove("opacity-50", "pointer-events-none")
    }
  }

  showError(message) {
    if (this.hasErrorSectionTarget) {
      this.errorSectionTarget.textContent = message
      this.errorSectionTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorSectionTarget) {
      this.errorSectionTarget.classList.add("hidden")
      this.errorSectionTarget.textContent = ""
    }
  }

  // --- HTTP Helpers ---

  async postJSON(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
      },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `HTTP ${response.status}`)
    }
    return response.json()
  }
}
